import { fetchWithToken } from "../services/api";
import { API_BASE_URL, ADMIN_API_BASE_URL } from "../config/api";

export const createUser = (user: User): Promise<User> =>
  fetchWithToken({
    method: "POST",
    endpoint: `${API_BASE_URL}/user/add`,
    payload: user,
  });

export const fetchUsers = (): Promise<User[]> =>
  fetchWithToken({
    method: "GET",
    endpoint: `${API_BASE_URL}/users`,
  });

export const fetchMe = (): Promise<User> =>
  fetchWithToken({
    method: "GET",
    endpoint: `${API_BASE_URL}/me`,
  });

// Region-agnostic profile fetch for the generic admin dashboard, which has no
// region in its host. Hits /admin/v1/me (no region enforcement) so a non-NZ user
// can be read before we know which region to route them to.
export const fetchMeGlobal = (shop?: string | null): Promise<User> => {
  const resolved =
    shop === undefined
      ? new URLSearchParams(window.location.search).get("shop")
      : shop;
  const qs = resolved ? `?shop=${encodeURIComponent(resolved)}` : "";
  return fetchWithToken({
    method: "GET",
    endpoint: `${ADMIN_API_BASE_URL}/me${qs}`,
  });
};

export const updateUser = (user: User): Promise<User> =>
  fetchWithToken({
    method: "PUT",
    endpoint: `${API_BASE_URL}/user/update`,
    payload: user,
  });

export const updateUserEnable = (id: number): Promise<User> =>
  fetchWithToken({
    method: "PUT",
    endpoint: `${API_BASE_URL}/user/${id}/enable`,
  });

export const updateUserDisable = (id: number): Promise<User> =>
  fetchWithToken({
    method: "PUT",
    endpoint: `${API_BASE_URL}/user/${id}/disable`,
  });

export const updateResetPassword = (id: number): Promise<GenericResponse> =>
  fetchWithToken({
    method: "POST",
    endpoint: `${API_BASE_URL}/user/${id}/resetPassword`,
  });

export const fetchBuckets = (): Promise<Bucket[]> =>
  fetchWithToken({
    method: "GET",
    endpoint: `${API_BASE_URL}/buckets`,
  });

export const fetchRoles = (): Promise<Role[]> =>
  fetchWithToken({
    method: "GET",
    endpoint: `${API_BASE_URL}/roles`,
  });

export const deleteUser = (userId: number): Promise<GenericResponse> =>
  fetchWithToken({
    method: "DELETE",
    endpoint: `${API_BASE_URL}/user/${userId}`,
  });

export const fetchTeam = (): Promise<User[]> =>
  fetchWithToken({
    method: "GET",
    endpoint: `${API_BASE_URL}/team`,
  });

export const updateMyPhoto = (photoUrl: string): Promise<User> =>
  fetchWithToken({
    method: "PUT",
    endpoint: `${API_BASE_URL}/me/photo`,
    payload: { photoUrl },
  });

/**
 * Save this user's theme against their account.
 *
 * An empty string clears it, which is how somebody goes back to following their machine — the
 * backend treats blank as "no preference" rather than rejecting it.
 */
export const updateMyTheme = (themePreference: "light" | "dark" | ""): Promise<User> =>
  fetchWithToken({
    method: "PUT",
    endpoint: `${API_BASE_URL}/me/theme`,
    payload: { themePreference },
  });

export const fetchActivityLog = (
  limit: number = 50,
  startDate?: string,
  endDate?: string,
  userId?: number,
  orderName?: string,
  gradingId?: number
): Promise<UserActivity[]> => {
  let endpoint = `${API_BASE_URL}/activity-log?limit=${limit}`;
  if (startDate) endpoint += `&start=${startDate}`;
  if (endDate) endpoint += `&end=${endDate}`;
  if (userId) endpoint += `&userId=${userId}`;
  if (orderName) endpoint += `&orderName=${encodeURIComponent(orderName)}`;
  if (gradingId) endpoint += `&gradingId=${gradingId}`;

  return fetchWithToken({
    method: "GET",
    endpoint,
  });
};
